import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import GenericTemplateAdvancedSearchContainerFormik from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateSearch/GenericTemplateAdvancedSearch/GenericTemplateAdvancedSearchContainerFormik';
import type { AdvancedSearchFilterTypes } from '~/components/GenericTemplates/types';
import { MOI_EMAIL_TEMPLATES_ROUTES } from '~/components/GenericTemplates/utils/routes';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { getAxiosParamsSerializer, reportAxiosError } from '~/Utils';
import cn from '~/Utils/cn';

import { saveMoiEmailTemplate, toggleMoiEmailTemplateEnabled } from './moiEmailApi';
import MoiEmailMethodsFilterFormik, { MOI_METHODS_FILTER_FIELD_ID } from './MoiEmailMethodsFilterFormik';
import MoiEmailTemplateBuilderContainer from './MoiEmailTemplateBuilderContainer';
import MoiEmailTemplateCardContainer from './MoiEmailTemplateCardContainer';
import type { MoiEmailTablePaginationProps } from './MoiEmailTemplatesTable';
import MoiEmailTemplatesTable from './MoiEmailTemplatesTable';
import type {
  MoiEmailTemplate,
  MoiEmailTemplateFormValues,
  MoiEmailTemplatesResponse,
  MoiMethod,
  MoiMethodsResponse,
} from './types';
import { moiEmailTemplateToMetadata, moiEmailTemplateToRowId, resolveMaterializedDefaultRowId } from './types';

const NO_ROW_SELECTED = 0;
// 'none' repeats list keys bare (moi_method_keys=a&moi_method_keys=b) — the shape the backend's
// spectree query schema parses (same serializer the generic templates table uses).
const FETCH_PARAMS_SERIALIZER = getAxiosParamsSerializer('none');

type MoiEmailSearchValues = AdvancedSearchFilterTypes & { moi_method_keys: string[] };

const MoiEmailTemplatesContainer: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();

  const [selectedRowId, setSelectedRowId] = useState<number>(NO_ROW_SELECTED);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  // Id of the template whose enable toggle is mid-flight, so the table can disable that switch until
  // the PATCH + reload settle — otherwise a rapid double-click fires overlapping toggle requests.
  const [togglingTemplateId, setTogglingTemplateId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useState<Partial<MoiEmailSearchValues>>({});
  const [paginationProps, setPaginationProps] = useState<MoiEmailTablePaginationProps>({ page: 0, limit: 20 });

  const { data, isLoading, isError, reloadData } = useDataFetcher<MoiEmailTemplatesResponse>(
    MOI_EMAIL_TEMPLATES_ROUTES.GET_TEMPLATES(organization.id),
    {
      // The table is 0-based; the endpoint is 1-based.
      params: { ...searchParams, page: paginationProps.page + 1, limit: paginationProps.limit },
      paramsSerializer: FETCH_PARAMS_SERIALIZER,
    }
  );

  // Methods for the search section's MOI-method filter (checkbox multi-select, OR semantics).
  const { data: methodsData, isLoading: isMethodsLoading } = useDataFetcher<MoiMethodsResponse>(
    MOI_EMAIL_TEMPLATES_ROUTES.GET_METHODS(organization.id)
  );
  const methods: MoiMethod[] = useMemo(() => methodsData?.methods ?? [], [methodsData]);

  // Unfiltered, unpaginated fetch feeding the builder's "method already mapped elsewhere" disable
  // logic — the table's list is only the current page/filter slice, and a conflict hidden by
  // pagination or an active search must still disable the method in the create/edit dialog.
  const { data: allTemplatesData, reloadData: reloadAllTemplates } = useDataFetcher<MoiEmailTemplatesResponse>(
    MOI_EMAIL_TEMPLATES_ROUTES.GET_TEMPLATES(organization.id)
  );
  const allTemplates: MoiEmailTemplate[] = useMemo(() => allTemplatesData?.templates ?? [], [allTemplatesData]);

  const handleAdvancedTemplatesSearch = (values: AdvancedSearchFilterTypes) => {
    // A new search starts from the first page.
    setPaginationProps((prev) => ({ ...prev, page: 0 }));
    setSearchParams({ ...values } as Partial<MoiEmailSearchValues>);
  };

  const templates: MoiEmailTemplate[] = useMemo(() => data?.templates ?? [], [data]);
  const rows = useMemo(() => templates.map(moiEmailTemplateToMetadata), [templates]);

  const reloadTable = async () => {
    await Promise.all([reloadData(), reloadAllTemplates()]);
  };

  const selectedTemplate = useMemo(
    () => templates.find((template) => moiEmailTemplateToRowId(template) === selectedRowId),
    [templates, selectedRowId]
  );

  // Once the synthetic Default is materialized, its row id changes from MOI_DEFAULT_ROW_ID to a real
  // id. Re-point the selection off the sentinel so the preview card and row highlight stay on the
  // Default instead of disappearing.
  useEffect(() => {
    const materializedDefaultRowId = resolveMaterializedDefaultRowId(selectedRowId, templates);
    if (materializedDefaultRowId !== null) {
      setSelectedRowId(materializedDefaultRowId);
    }
  }, [templates, selectedRowId]);

  // Templates feed the methods "already mapped elsewhere" disable logic; a template being edited
  // passes the rest (excluding itself) so its own methods stay selectable. Disabled templates are
  // included on purpose — the backend's scope-uniqueness check spans enabled and disabled templates
  // alike, so a method owned by a disabled same-scope template would still 409 on save.
  const otherTemplatesForSelected = useMemo(
    () => allTemplates.filter((template) => moiEmailTemplateToRowId(template) !== selectedRowId),
    [allTemplates, selectedRowId]
  );

  const handleCreate = async (values: MoiEmailTemplateFormValues) => {
    try {
      await saveMoiEmailTemplate(organization.id, { ...values, id: null });
      await reloadTable();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  const handleToggleEnabled = async (templateId: number, newStatus: boolean) => {
    setTogglingTemplateId(templateId);
    try {
      await toggleMoiEmailTemplateEnabled(organization.id, templateId, newStatus);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setTogglingTemplateId(null);
    }
  };

  return (
    <div>
      <div className="m-12 flex items-center justify-end p-12">
        <Button color="primary" onClick={() => setIsCreateDialogOpen(true)}>
          <AddIcon />
          Add template
        </Button>
      </div>
      {/* Below the actions row, above the table — the same order as the other generic templates screens. */}
      <GenericTemplateAdvancedSearchContainerFormik
        handleSubmit={handleAdvancedTemplatesSearch}
        isLoading={isLoading}
        additionalInitialValues={{ [MOI_METHODS_FILTER_FIELD_ID]: [] }}
        additionalValidation={{ [MOI_METHODS_FILTER_FIELD_ID]: Yup.array().of(Yup.string()) }}
        additionalFilters={<MoiEmailMethodsFilterFormik methods={methods} optionsLoading={isMethodsLoading} />}
      />
      <div className={cn('grid gap-4', selectedTemplate ? 'grid-cols-12' : 'grid-cols-1')}>
        <div className="col-span-8">
          {isLoading || isError ? (
            <SkeletonTable rowsCount={10} columnsCount={6} isError={isError} />
          ) : (
            <MoiEmailTemplatesTable
              templates={rows}
              templatesCount={data?.count ?? 0}
              paginationProps={paginationProps}
              onUpdatePaginationProps={setPaginationProps}
              onSelectTemplate={setSelectedRowId}
              onToggleEnabled={handleToggleEnabled}
              selectedRowId={selectedRowId}
              togglingTemplateId={togglingTemplateId}
            />
          )}
        </div>
        {selectedTemplate ? (
          <div className="col-span-4">
            {/* Key by the selected row so the card (and its once-initialised Editor) remounts on
                selection change — otherwise switching to the id-less Default keeps stale content. */}
            <MoiEmailTemplateCardContainer
              key={selectedRowId}
              template={selectedTemplate}
              otherTemplates={otherTemplatesForSelected}
              reloadTable={reloadTable}
            />
          </div>
        ) : null}
      </div>
      {isCreateDialogOpen ? (
        <MoiEmailTemplateBuilderContainer
          otherTemplates={allTemplates}
          handleClose={() => setIsCreateDialogOpen(false)}
          handleSubmit={handleCreate}
        />
      ) : null}
    </div>
  );
};

export default MoiEmailTemplatesContainer;
